import { useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { ButtonPrimary } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { Lock, Plus, Trash2, Send, CheckCircle2, XCircle, FileText, Upload, Printer, Package, PackageCheck } from "lucide-react";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STATUS_STYLES = {
    pending_review: "bg-amber-50 text-amber-700 border-amber-200",
    awaiting_payment: "bg-blue-50 text-blue-700 border-blue-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    packing: "bg-[#A68F6C]/10 text-[#8a764f] border-[#A68F6C]/40",
    ready_to_ship: "bg-[#7A2230]/10 text-[#7A2230] border-[#7A2230]/30",
    shipped: "bg-slate-800 text-ivory border-slate-800",
    cancelled: "bg-stone-100 text-stone-500 border-stone-200",
};

const STATUS_LABELS = {
    pending_review: "Pending review",
    awaiting_payment: "Awaiting payment",
    paid: "Paid",
    packing: "Packing",
    ready_to_ship: "Ready to ship",
    shipped: "Shipped",
    cancelled: "Cancelled",
};

const escHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function printPackingSlip(order) {
    const s = order.shipping || {};
    const address = [s.line1, s.line2, [s.city, s.state].filter(Boolean).join(", "), [s.postal_code, s.country].filter(Boolean).join(" ")].filter(Boolean);
    const rows = order.items
        .map(
            (i) => `<tr>
                <td style="padding:10px 12px;border-bottom:1px solid #E8E2D5;width:34px;text-align:center;font-family:monospace;font-size:15px">☐</td>
                <td style="padding:10px 12px;border-bottom:1px solid #E8E2D5;font-size:13px;color:#2C3A4E">${escHtml(i.name)}${i.variant_label ? ` — ${escHtml(i.variant_label)}` : ""}${i.sku ? `<br><span style="font-size:11px;color:#98A0AB">SKU ${escHtml(i.sku)}</span>` : ""}${i.lot_batch ? `<br><span style="font-size:11px;color:#98A0AB">Lot ${escHtml(i.lot_batch)}</span>` : ""}</td>
                <td style="padding:10px 12px;border-bottom:1px solid #E8E2D5;font-size:13px;text-align:right;color:#2C3A4E">× ${i.qty}</td>
            </tr>`
        )
        .join("");
    const win = window.open("", "_blank", "width=800,height=1000");
    if (!win) {
        toast.error("Popup blocked", { description: "Allow popups for this site to print packing slips." });
        return;
    }
    win.document.write(`<!DOCTYPE html><html><head><title>Packing Slip — ${escHtml(order.order_id)}</title></head>
    <body style="margin:0;background:#fff;font-family:Arial,sans-serif">
        <div style="max-width:640px;margin:40px auto;padding:40px;border:1px solid #E3DCCB">
            <p style="color:#A68F6C;letter-spacing:3px;font-size:11px;text-transform:uppercase;margin:0">Grayhaven Research</p>
            <h1 style="font-family:Georgia,serif;font-weight:normal;font-size:26px;margin:10px 0 4px;color:#2C3A4E">Packing Slip</h1>
            <p style="font-size:12px;color:#5B6572;margin:0 0 28px">Order <strong>${escHtml(order.order_id)}</strong> · ${new Date(order.created_at).toLocaleDateString()}</p>
            <p style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#98A0AB;margin:0 0 6px">Ship To</p>
            <p style="font-size:14px;line-height:1.7;color:#2C3A4E;margin:0 0 28px"><strong>${escHtml(order.name)}</strong><br>${address.map(escHtml).join("<br>")}</p>
            <p style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#98A0AB;margin:0 0 6px">Contents</p>
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-top:1px solid #E8E2D5">${rows}</table>
            <p style="font-size:11px;color:#98A0AB;margin:28px 0 0;border-top:1px solid #E8E2D5;padding-top:16px">
                All materials are supplied strictly for laboratory research use only. Not for human or veterinary use.
                This packing slip does not show prices — see the order confirmation email for billing details.
            </p>
        </div>
        <script>window.onload = () => window.print();</script>
    </body></html>`);
    win.document.close();
}

function ProductEditor({ product, onSaved }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState(product.name);
    const [imageUrl, setImageUrl] = useState(product.image_url?.startsWith("data:") ? "" : product.image_url || "");
    const [description, setDescription] = useState(product.description || "");
    const [variants, setVariants] = useState(
        product.variants.map((v) => ({ ...v, dirty: false }))
    );
    const [busy, setBusy] = useState(false);
    const [newVariant, setNewVariant] = useState({ label: "", price: "", inventory: "" });
    const [confirmRemove, setConfirmRemove] = useState(false);

    const removeProduct = async () => {
        setBusy(true);
        try {
            await api.delete(`/admin/products/${product.id}`);
            toast.success("Product removed", {
                description: `${product.name} no longer appears in the catalog. Past orders are unaffected.`,
            });
            onSaved();
        } catch (err) {
            toast.error("Could not remove product", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setBusy(false);
            setConfirmRemove(false);
        }
    };

    const addVariant = async () => {
        if (!newVariant.label.trim() || !newVariant.price.trim()) {
            toast.error("Missing details", { description: "Enter a size (e.g. 30 mg) and a price." });
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/products/${product.id}/variants`, {
                label: newVariant.label.trim(),
                price: newVariant.price.trim(),
                inventory: parseInt(newVariant.inventory, 10) || 0,
            });
            toast.success("Variant added", {
                description: `${newVariant.label.trim()} is live on ${product.name}. Upload its COA in the COA Library below if you have one.`,
            });
            setNewVariant({ label: "", price: "", inventory: "" });
            onSaved();
        } catch (err) {
            toast.error("Could not add variant", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setBusy(false);
        }
    };

    const setVariant = (i, patch) =>
        setVariants((vs) => vs.map((v, j) => (j === i ? { ...v, ...patch, dirty: true } : v)));

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/admin/products/${product.id}`, {
                name,
                image_url: imageUrl,
                description,
                variants: variants.filter((v) => v.dirty).map(({ label, price, inventory, available }) => ({ label, price, inventory, available })),
            });
            toast.success("Product saved", { description: product.name });
            onSaved();
        } catch (err) {
            toast.error("Could not save product", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setBusy(false);
        }
    };

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const data = new FormData();
        data.append("file", file);
        setBusy(true);
        try {
            await api.post(`/admin/products/${product.id}/image`, data);
            toast.success("Photo updated", { description: product.name });
            onSaved();
        } catch (err) {
            toast.error("Upload failed", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-stone-200" data-testid={`admin-product-${product.id}`}>
            <div className="flex items-center">
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    data-testid={`admin-product-toggle-${product.id}`}
                    className="flex-1 min-w-0 flex items-center gap-4 p-5 text-left hover:bg-surface2/60 transition-colors"
                >
                    <img src={product.image_url || "/assets/vial-product.jpg"} alt="" className="h-12 w-9 object-cover border border-stone-200 shrink-0" />
                    <span className="flex-1 min-w-0">
                        <span className="font-serif text-lg text-brand block truncate">{product.name}</span>
                        <span className="text-[0.6rem] uppercase tracking-[0.2em] text-slate-400">{product.category}</span>
                    </span>
                    <span className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine shrink-0">
                        {open ? "Close" : "Edit"}
                    </span>
                </button>
                {confirmRemove ? (
                    <span className="shrink-0 flex items-center gap-3 pr-5">
                        <button
                            type="button"
                            onClick={removeProduct}
                            disabled={busy}
                            data-testid={`admin-product-remove-confirm-${product.id}`}
                            className="text-[0.6rem] uppercase tracking-[0.15em] font-semibold text-red-700 hover:text-red-800 transition-colors"
                        >
                            {busy ? "Removing…" : "Confirm remove"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirmRemove(false)}
                            data-testid={`admin-product-remove-cancel-${product.id}`}
                            className="text-[0.6rem] uppercase tracking-[0.15em] text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            Keep
                        </button>
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={() => setConfirmRemove(true)}
                        aria-label={`Remove ${product.name}`}
                        data-testid={`admin-product-remove-${product.id}`}
                        className="shrink-0 p-5 text-slate-300 hover:text-red-600 transition-colors"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                )}
            </div>

            {open && (
                <div className="border-t border-stone-200 p-6 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <label className="block">
                            <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Product name</span>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid={`admin-product-name-${product.id}`}
                                className="mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Photo URL</span>
                            <input
                                value={imageUrl}
                                onChange={(e) => setImageUrl(e.target.value)}
                                placeholder="/assets/products/… or https://…"
                                data-testid={`admin-product-image-${product.id}`}
                                className="mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                            />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Description</span>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            data-testid={`admin-product-description-${product.id}`}
                            className="mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                        />
                    </label>
                    <label className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine cursor-pointer hover:text-wine-deep transition-colors">
                        <Plus className="h-3.5 w-3.5" /> Upload new photo
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={uploadImage}
                            data-testid={`admin-product-upload-${product.id}`}
                            className="hidden"
                        />
                    </label>

                    <div>
                        <p className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Variants</p>
                        <div className="mt-3 overflow-x-auto">
                            <table className="w-full text-sm" data-testid={`admin-product-variants-${product.id}`}>
                                <thead>
                                    <tr className="text-left text-[0.6rem] uppercase tracking-[0.18em] text-slate-400">
                                        <th className="pb-2 pr-4">Size</th>
                                        <th className="pb-2 pr-4">Price</th>
                                        <th className="pb-2 pr-4">Inventory</th>
                                        <th className="pb-2">In stock</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {variants.map((v, i) => (
                                        <tr key={v.label} className="border-t border-stone-100">
                                            <td className="py-2.5 pr-4 text-slate-700">{v.label}</td>
                                            <td className="py-2.5 pr-4">
                                                <input
                                                    value={v.price || ""}
                                                    onChange={(e) => setVariant(i, { price: e.target.value })}
                                                    data-testid={`admin-price-${product.id}-${i}`}
                                                    className="w-24 border border-stone-200 px-3 py-1.5 text-sm focus:border-wine/50 focus:outline-none"
                                                />
                                            </td>
                                            <td className="py-2.5 pr-4">
                                                <input
                                                    type="number" min="0"
                                                    value={v.inventory ?? 0}
                                                    onChange={(e) => setVariant(i, { inventory: parseInt(e.target.value, 10) || 0 })}
                                                    data-testid={`admin-inventory-${product.id}-${i}`}
                                                    className="w-24 border border-stone-200 px-3 py-1.5 text-sm focus:border-wine/50 focus:outline-none"
                                                />
                                            </td>
                                            <td className="py-2.5">
                                                <input
                                                    type="checkbox"
                                                    checked={!!v.available}
                                                    onChange={(e) => setVariant(i, { available: e.target.checked })}
                                                    data-testid={`admin-available-${product.id}-${i}`}
                                                    className="h-4 w-4 accent-[#7A2230]"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-dashed border-stone-200 pt-4">
                            <label className="block">
                                <span className="text-[0.6rem] uppercase tracking-[0.18em] text-slate-400">New size</span>
                                <input
                                    value={newVariant.label}
                                    onChange={(e) => setNewVariant((v) => ({ ...v, label: e.target.value }))}
                                    placeholder="30 mg"
                                    maxLength={40}
                                    data-testid={`admin-new-variant-label-${product.id}`}
                                    className="mt-1.5 w-28 border border-stone-200 px-3 py-1.5 text-sm focus:border-wine/50 focus:outline-none"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[0.6rem] uppercase tracking-[0.18em] text-slate-400">Price</span>
                                <input
                                    value={newVariant.price}
                                    onChange={(e) => setNewVariant((v) => ({ ...v, price: e.target.value }))}
                                    placeholder="105"
                                    data-testid={`admin-new-variant-price-${product.id}`}
                                    className="mt-1.5 w-24 border border-stone-200 px-3 py-1.5 text-sm focus:border-wine/50 focus:outline-none"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[0.6rem] uppercase tracking-[0.18em] text-slate-400">Inventory</span>
                                <input
                                    type="number" min="0"
                                    value={newVariant.inventory}
                                    onChange={(e) => setNewVariant((v) => ({ ...v, inventory: e.target.value }))}
                                    placeholder="0"
                                    data-testid={`admin-new-variant-inventory-${product.id}`}
                                    className="mt-1.5 w-24 border border-stone-200 px-3 py-1.5 text-sm focus:border-wine/50 focus:outline-none"
                                />
                            </label>
                            <button
                                type="button"
                                onClick={addVariant}
                                disabled={busy}
                                data-testid={`admin-new-variant-add-${product.id}`}
                                className="inline-flex items-center gap-1.5 border border-wine/40 px-4 py-2 text-[0.65rem] uppercase tracking-[0.18em] font-semibold text-wine hover:bg-wine hover:text-ivory transition-colors duration-300"
                            >
                                <Plus className="h-3.5 w-3.5" /> Add variant
                            </button>
                        </div>
                    </div>

                    <ButtonPrimary onClick={save} disabled={busy} data-testid={`admin-product-save-${product.id}`}>
                        {busy ? "Saving…" : "Save Product"}
                    </ButtonPrimary>
                </div>
            )}
        </div>
    );
}

function OrderCard({ order, onAction }) {
    const [link, setLink] = useState(order.payment_link || "");
    const [tracking, setTracking] = useState(order.tracking_number || "");
    const [busy, setBusy] = useState(false);

    const act = async (action, body) => {
        setBusy(true);
        try {
            await api.post(`/admin/orders/${order.order_id}/${action}`, body);
            toast.success("Order updated", { description: order.order_id });
            onAction();
        } catch (err) {
            toast.error("Action failed", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-stone-200 p-6" data-testid={`admin-order-${order.order_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="font-serif text-lg text-brand">{order.order_id}</p>
                    <p className="mt-1 text-xs text-mist">
                        {order.name} · {order.email} · {new Date(order.created_at).toLocaleString()}
                    </p>
                    <p className="mt-2 text-xs text-slate-600">
                        {order.items.map((i) => `${i.name}${i.variant_label ? ` ${i.variant_label}` : ""} ×${i.qty}`).join(", ")}
                    </p>
                    <p className="mt-1 text-[0.65rem] text-slate-400">
                        Subtotal {money(order.subtotal_cents)}
                        {order.discount_cents ? ` · Discount −${money(order.discount_cents)}` : ""}
                        {order.shipping_cents ? ` · Shipping ${money(order.shipping_cents)}` : ""}
                        {order.tax_cents ? ` · Tax ${money(order.tax_cents)}` : ""}
                        {order.payment_link_fee_cents ? ` · Payment-link fee ${money(order.payment_link_fee_cents)}` : ""}
                    </p>
                </div>
                <div className="text-right">
                    <p className="font-serif text-xl text-wine">{money(order.total_cents)}</p>
                    <span
                        className={`mt-2 inline-block border px-3 py-1 text-[0.6rem] uppercase tracking-[0.18em] font-semibold ${STATUS_STYLES[order.status] || ""}`}
                        data-testid={`admin-order-status-${order.order_id}`}
                    >
                        {STATUS_LABELS[order.status] || order.status}
                    </span>
                </div>
            </div>

            {(order.status === "pending_review" || order.status === "awaiting_payment") && (
                <div className="mt-5 pt-5 border-t border-stone-200 flex flex-col sm:flex-row gap-3">
                    <input
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        placeholder="https:// payment link for this order"
                        data-testid={`admin-link-input-${order.order_id}`}
                        className="flex-1 border border-stone-200 bg-white px-4 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-wine/50 focus:outline-none"
                    />
                    <div className="flex gap-3">
                        <button
                            type="button"
                            disabled={busy || !link.trim()}
                            onClick={() => act("payment-link", { url: link.trim() })}
                            data-testid={`admin-send-link-${order.order_id}`}
                            className="inline-flex items-center gap-2 bg-wine text-white px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.15em] font-semibold hover:bg-wine-deep transition-colors duration-300 disabled:opacity-40"
                        >
                            <Send className="h-3.5 w-3.5" /> Send Link
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => act("mark-paid")}
                            data-testid={`admin-mark-paid-${order.order_id}`}
                            className="inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.15em] font-semibold hover:bg-emerald-50 transition-colors duration-300 disabled:opacity-40"
                        >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Mark Paid
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => act("cancel")}
                            data-testid={`admin-cancel-${order.order_id}`}
                            className="inline-flex items-center gap-2 border border-stone-300 text-stone-500 px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.15em] font-semibold hover:text-red-600 hover:border-red-300 transition-colors duration-300 disabled:opacity-40"
                        >
                            <XCircle className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}
            {order.status === "awaiting_payment" && order.payment_link && (
                <p className="mt-3 text-[0.65rem] text-slate-400 truncate">
                    Sent link: {order.payment_link} · {new Date(order.payment_link_sent_at).toLocaleString()}
                </p>
            )}
            {["paid", "packing", "ready_to_ship", "shipped"].includes(order.status) && (
                <div className="mt-5 pt-5 border-t border-stone-200 flex flex-wrap items-center gap-3">
                    {order.status === "paid" && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => act("fulfillment", { status: "packing" })}
                            data-testid={`admin-start-packing-${order.order_id}`}
                            className="inline-flex items-center gap-2 border border-[#A68F6C]/60 text-[#8a764f] px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.15em] font-semibold hover:bg-[#A68F6C]/10 transition-colors duration-300 disabled:opacity-40"
                        >
                            <Package className="h-3.5 w-3.5" /> Start Packing
                        </button>
                    )}
                    {order.status === "packing" && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => act("fulfillment", { status: "ready_to_ship" })}
                            data-testid={`admin-ready-ship-${order.order_id}`}
                            className="inline-flex items-center gap-2 bg-wine text-white px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.15em] font-semibold hover:bg-wine-deep transition-colors duration-300 disabled:opacity-40"
                        >
                            <PackageCheck className="h-3.5 w-3.5" /> Ready to Ship
                        </button>
                    )}
                    {order.status === "ready_to_ship" && (
                        <>
                            <input
                                value={tracking}
                                onChange={(e) => setTracking(e.target.value)}
                                placeholder="Tracking number"
                                maxLength={120}
                                data-testid={`admin-tracking-${order.order_id}`}
                                className="flex-1 min-w-44 border border-stone-200 bg-white px-4 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-wine/50 focus:outline-none"
                            />
                            <button
                                type="button"
                                disabled={busy || !tracking.trim()}
                                onClick={() => act("fulfillment", { status: "shipped", tracking: tracking.trim() })}
                                data-testid={`admin-mark-shipped-${order.order_id}`}
                                className="inline-flex items-center gap-2 bg-slate-800 text-ivory px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.15em] font-semibold hover:bg-slate-900 transition-colors duration-300 disabled:opacity-40"
                            >
                                <Send className="h-3.5 w-3.5" /> Mark Shipped
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={() => printPackingSlip(order)}
                        data-testid={`admin-print-slip-${order.order_id}`}
                        className="inline-flex items-center gap-2 border border-stone-300 text-slate-600 px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.15em] font-semibold hover:border-wine/50 hover:text-wine transition-colors duration-300"
                    >
                        <Printer className="h-3.5 w-3.5" /> Packing Slip
                    </button>
                </div>
            )}
            {order.status === "shipped" && order.tracking_number && (
                <p className="mt-3 text-[0.65rem] text-slate-400" data-testid={`admin-tracking-shown-${order.order_id}`}>
                    Tracking: {order.tracking_number} · shipped {new Date(order.shipped_at).toLocaleString()} · customer emailed automatically
                </p>
            )}
        </div>
    );
}

function ProductCreator({ onSaved }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", category: "Research Compounds", description: "", unit: "1 vial", variant_label: "", price: "", inventory: "" });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const create = async () => {
        if (!form.name.trim() || !form.variant_label.trim() || !form.price.trim()) {
            toast.error("Missing details", { description: "Name, first variant size, and price are required." });
            return;
        }
        setBusy(true);
        try {
            const res = await api.post("/admin/products", {
                name: form.name.trim(),
                category: form.category,
                description: form.description.trim(),
                unit: form.unit.trim() || "1 vial",
                variant_label: form.variant_label.trim(),
                price: form.price.trim(),
                inventory: parseInt(form.inventory, 10) || 0,
            });
            toast.success("Product created", { description: `${form.name.trim()} is live. Open it below to add a photo and more variants.` });
            setForm({ name: "", category: "Research Compounds", description: "", unit: "1 vial", variant_label: "", price: "", inventory: "" });
            setOpen(false);
            onSaved();
        } catch (err) {
            toast.error("Could not create product", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none";
    const labelCls = "text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold";

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="admin-product-add-btn"
                className="inline-flex items-center gap-2 border border-wine/40 px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.18em] font-semibold text-wine hover:bg-wine hover:text-ivory transition-colors duration-300"
            >
                <Plus className="h-3.5 w-3.5" /> Add product
            </button>
        );
    }

    return (
        <div className="border border-wine/30 bg-white p-6" data-testid="admin-product-create-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                    <span className={labelCls}>Product name</span>
                    <input value={form.name} onChange={set("name")} maxLength={120} placeholder="BAC Water" data-testid="admin-new-product-name" className={inputCls} />
                </label>
                <label className="block">
                    <span className={labelCls}>Category</span>
                    <select value={form.category} onChange={set("category")} data-testid="admin-new-product-category" className={inputCls}>
                        <option>Research Compounds</option>
                        <option>Laboratory Supplies</option>
                    </select>
                </label>
                <label className="block sm:col-span-2">
                    <span className={labelCls}>Description (optional)</span>
                    <textarea value={form.description} onChange={set("description")} rows={2} maxLength={2000} data-testid="admin-new-product-description" className={inputCls} />
                </label>
                <label className="block">
                    <span className={labelCls}>Unit</span>
                    <input value={form.unit} onChange={set("unit")} maxLength={30} placeholder="1 vial" data-testid="admin-new-product-unit" className={inputCls} />
                </label>
                <label className="block">
                    <span className={labelCls}>First variant size</span>
                    <input value={form.variant_label} onChange={set("variant_label")} maxLength={40} placeholder="3 mL" data-testid="admin-new-product-variant" className={inputCls} />
                </label>
                <label className="block">
                    <span className={labelCls}>Price</span>
                    <input value={form.price} onChange={set("price")} placeholder="5.99" data-testid="admin-new-product-price" className={inputCls} />
                </label>
                <label className="block">
                    <span className={labelCls}>Inventory</span>
                    <input type="number" min="0" value={form.inventory} onChange={set("inventory")} placeholder="0" data-testid="admin-new-product-inventory" className={inputCls} />
                </label>
            </div>
            <p className="mt-4 text-[0.65rem] text-slate-400">
                New products go live immediately with the standard vial image. Open the product below afterwards to upload a photo or add more variants.
            </p>
            <div className="mt-5 flex items-center gap-4">
                <ButtonPrimary onClick={create} disabled={busy} data-testid="admin-new-product-save-btn">
                    {busy ? "Creating…" : "Create product"}
                </ButtonPrimary>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    data-testid="admin-new-product-cancel-btn"
                    className="text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-slate-600 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

function CoaLibrary({ products, documents, onChanged }) {
    const [materialId, setMaterialId] = useState("");
    const [variantLabel, setVariantLabel] = useState("");
    const [lot, setLot] = useState("");
    const [issued, setIssued] = useState("");
    const [title, setTitle] = useState("");
    const [titleTouched, setTitleTouched] = useState(false);
    const [file, setFile] = useState(null);
    const [fileKey, setFileKey] = useState(0);
    const [busy, setBusy] = useState(false);
    const [confirmingId, setConfirmingId] = useState(null);
    const [removingId, setRemovingId] = useState(null);

    const removeDoc = async (doc) => {
        setRemovingId(doc.id);
        try {
            await api.delete(`/admin/documents/${doc.id}`);
            toast.success("COA unpublished", { description: `${doc.title} was removed from the library.` });
            setConfirmingId(null);
            onChanged();
        } catch (err) {
            toast.error("Could not remove", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setRemovingId(null);
        }
    };

    const material = (products || []).find((p) => p.id === materialId);
    const variants = material?.variants || [];
    const suggested = material
        ? `Certificate of Analysis — ${material.name}${variantLabel ? ` ${variantLabel}` : ""}`
        : "";

    useEffect(() => {
        if (!titleTouched) setTitle(suggested);
    }, [suggested, titleTouched]);

    const upload = async () => {
        if (!materialId || !lot.trim() || !file) {
            toast.error("Missing details", { description: "Choose a product, enter the lot/batch, and pick a PDF." });
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("material_id", materialId);
            fd.append("variant_label", variantLabel);
            fd.append("batch_lot", lot.trim());
            fd.append("issued_date", issued);
            fd.append("title", title.trim());
            await api.post("/admin/documents/upload", fd);
            toast.success("COA uploaded", { description: "It is now live in the Testing & COAs library." });
            setVariantLabel("");
            setLot("");
            setIssued("");
            setTitle("");
            setTitleTouched(false);
            setFile(null);
            setFileKey((k) => k + 1);
            onChanged();
        } catch (err) {
            toast.error("Upload failed", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "mt-2 w-full border border-stone-200 px-4 py-3 text-sm focus:border-wine/50 focus:outline-none";
    const labelCls = "text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold";

    return (
        <div className="bg-white border border-stone-200 p-7 sm:p-10" data-testid="admin-coa-section">
            <p className="eyebrow">COA Library</p>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed max-w-2xl">
                Upload a genuine Certificate of Analysis PDF. Link it to a product variant to show its lot
                number and a View COA button on the product page, or add it to the public library only.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <label className="block">
                    <span className={labelCls}>Product</span>
                    <select
                        value={materialId}
                        onChange={(e) => { setMaterialId(e.target.value); setVariantLabel(""); }}
                        data-testid="admin-coa-product-select"
                        className={inputCls}
                    >
                        <option value="">Choose a product…</option>
                        {(products || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className={labelCls}>Variant (optional)</span>
                    <select
                        value={variantLabel}
                        onChange={(e) => setVariantLabel(e.target.value)}
                        disabled={!material}
                        data-testid="admin-coa-variant-select"
                        className={inputCls}
                    >
                        <option value="">Library only — no variant link</option>
                        {variants.map((v) => (
                            <option key={v.label} value={v.label}>{v.label}{v.coa_document_id ? " (has COA)" : ""}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className={labelCls}>Batch / Lot</span>
                    <input
                        type="text" maxLength={60} value={lot} onChange={(e) => setLot(e.target.value)}
                        placeholder="Exactly as shown on the COA"
                        data-testid="admin-coa-lot-input"
                        className={inputCls}
                    />
                </label>
                <label className="block">
                    <span className={labelCls}>Issued date (optional)</span>
                    <input
                        type="date" value={issued} onChange={(e) => setIssued(e.target.value)}
                        data-testid="admin-coa-date-input"
                        className={inputCls}
                    />
                </label>
                <label className="block sm:col-span-2">
                    <span className={labelCls}>Library title</span>
                    <input
                        type="text" maxLength={150} value={title}
                        onChange={(e) => { setTitle(e.target.value); setTitleTouched(true); }}
                        placeholder="Certificate of Analysis — …"
                        data-testid="admin-coa-title-input"
                        className={inputCls}
                    />
                </label>
                <label className="block sm:col-span-2">
                    <span className={labelCls}>PDF file</span>
                    <input
                        key={fileKey}
                        type="file" accept="application/pdf"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        data-testid="admin-coa-file-input"
                        className="mt-2 w-full border border-dashed border-stone-300 px-4 py-3 text-sm text-slate-600 file:mr-4 file:border-0 file:bg-surface2 file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.15em] file:text-slate-600"
                    />
                </label>
            </div>

            <div className="mt-6">
                <ButtonPrimary onClick={upload} disabled={busy} data-testid="admin-coa-upload-btn">
                    <Upload className="h-4 w-4" />
                    {busy ? "Uploading…" : "Upload COA"}
                </ButtonPrimary>
            </div>

            <div className="mt-10 border-t border-stone-200 pt-6">
                <p className={labelCls}>Published documents ({(documents || []).length})</p>
                <ul className="mt-4 divide-y divide-stone-100" data-testid="admin-coa-list">
                    {(documents || []).map((d) => (
                        <li key={d.id} className="py-3 flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-700 min-w-0 truncate">
                                <FileText className="inline h-3.5 w-3.5 text-gold mr-2 -mt-0.5" />
                                {d.material_name} — {d.title.replace(/^Certificate of Analysis — /, "")} · Lot {d.batch_lot}
                            </span>
                            {d.file_url && (
                                <a
                                    href={d.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`admin-coa-view-${d.id}`}
                                    className="shrink-0 text-xs uppercase tracking-[0.15em] text-wine hover:text-wine-deep transition-colors"
                                >
                                    View
                                </a>
                            )}
                            {confirmingId === d.id ? (
                                <span className="shrink-0 flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => removeDoc(d)}
                                        disabled={removingId === d.id}
                                        data-testid={`admin-coa-remove-confirm-${d.id}`}
                                        className="text-xs uppercase tracking-[0.15em] text-red-700 hover:text-red-800 font-semibold transition-colors"
                                    >
                                        {removingId === d.id ? "Removing…" : "Confirm remove"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmingId(null)}
                                        data-testid={`admin-coa-remove-cancel-${d.id}`}
                                        className="text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        Keep
                                    </button>
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setConfirmingId(d.id)}
                                    data-testid={`admin-coa-remove-${d.id}`}
                                    className="shrink-0 text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-red-700 transition-colors"
                                >
                                    Remove
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function AiAssistant({ products }) {
    const [kind, setKind] = useState("product_description");
    const [productId, setProductId] = useState("");
    const [context, setContext] = useState("");
    const [output, setOutput] = useState("");
    const [busy, setBusy] = useState(false);

    const KINDS = [
        ["product_description", "Product Description"],
        ["customer_reply", "Customer Reply"],
        ["freeform", "Improve Text"],
    ];

    const generate = async () => {
        if (busy) return;
        setOutput("");
        setBusy(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai/draft`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ kind, product_id: productId || null, context: context || null }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Generation failed");
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split("\n\n");
                buffer = events.pop();
                for (const ev of events) {
                    if (!ev.startsWith("data: ")) continue;
                    const payload = ev.slice(6);
                    if (payload === "[DONE]") continue;
                    try {
                        const parsed = JSON.parse(payload);
                        if (parsed.error) throw new Error(parsed.error);
                        if (parsed.t) setOutput((o) => o + parsed.t);
                    } catch (e) {
                        if (e.message !== "Unexpected token") throw e;
                    }
                }
            }
        } catch (err) {
            toast.error("AI draft failed", { description: err.message });
        } finally {
            setBusy(false);
        }
    };

    const saveToProduct = async () => {
        try {
            await api.put(`/admin/products/${productId}`, { description: output });
            toast.success("Description saved to product");
        } catch (err) {
            toast.error("Could not save", { description: err.response?.data?.detail || "Please try again." });
        }
    };

    return (
        <div className="bg-white border border-stone-200 p-7 sm:p-10" data-testid="admin-ai-assistant">
            <p className="eyebrow">AI Assistant</p>
            <p className="mt-3 text-xs text-mist leading-relaxed max-w-2xl">
                Drafts follow Grayhaven rules automatically — laboratory research use only, never any dosing,
                usage, or human/veterinary guidance.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
                {KINDS.map(([k, label]) => (
                    <button
                        key={k}
                        type="button"
                        onClick={() => setKind(k)}
                        data-testid={`ai-kind-${k}`}
                        className={`px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.2em] font-semibold border transition-colors duration-300 ${
                            kind === k ? "bg-wine text-white border-wine" : "bg-white text-brand border-taupe/60 hover:border-wine/50 hover:text-wine"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {kind === "product_description" && (
                <select
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    data-testid="ai-product-select"
                    className="mt-5 w-full sm:w-80 border border-stone-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-wine/50 focus:outline-none"
                >
                    <option value="">Choose a product…</option>
                    {(products || []).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            )}

            {kind !== "product_description" && (
                <textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    rows={4}
                    placeholder={kind === "customer_reply" ? "Paste the customer's message…" : "Paste the text to improve…"}
                    data-testid="ai-context-input"
                    className="mt-5 w-full border border-stone-200 px-4 py-3 text-sm focus:border-wine/50 focus:outline-none"
                />
            )}

            <div className="mt-5">
                <ButtonPrimary
                    onClick={generate}
                    disabled={busy || (kind === "product_description" ? !productId : !context.trim())}
                    data-testid="ai-generate-btn"
                >
                    {busy ? "Drafting…" : "Generate Draft"}
                </ButtonPrimary>
            </div>

            {(output || busy) && (
                <div className="mt-6">
                    <textarea
                        value={output}
                        onChange={(e) => setOutput(e.target.value)}
                        rows={6}
                        data-testid="ai-output"
                        className="w-full border border-gold/40 bg-[#FDFAF5] px-4 py-3 text-sm leading-relaxed text-slate-700 focus:border-wine/50 focus:outline-none"
                    />
                    <div className="mt-3 flex flex-wrap gap-4">
                        <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(output); toast.success("Copied"); }}
                            data-testid="ai-copy-btn"
                            className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine hover:text-wine-deep transition-colors"
                        >
                            Copy
                        </button>
                        {kind === "product_description" && productId && output.trim() && (
                            <button
                                type="button"
                                onClick={saveToProduct}
                                data-testid="ai-save-to-product-btn"
                                className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine hover:text-wine-deep transition-colors"
                            >
                                Save to Product
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Admin() {
    const { user } = useAuth();
    const [orders, setOrders] = useState(null);
    const [settings, setSettings] = useState(null);
    const [products, setProducts] = useState(null);
    const [documents, setDocuments] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        api.get("/admin/orders").then((res) => setOrders(res.data)).catch(() => setOrders([]));
        api.get("/admin/settings").then((res) => setSettings(res.data)).catch(() => {});
        api.get("/admin/products").then((res) => setProducts(res.data)).catch(() => setProducts([]));
        api.get("/documents").then((res) => setDocuments(res.data)).catch(() => setDocuments([]));
    }, []);

    useEffect(() => {
        if (user?.role === "admin") load();
    }, [user, load]);

    if (!user || user.role !== "admin") {
        return (
            <div data-testid="admin-page-denied">
                <PageHeader eyebrow="Administration" title="Admin" />
                <section className="pb-24 px-6">
                    <div className="max-w-xl mx-auto border border-dashed border-gold/30 rounded-sm py-16 px-8 text-center bg-white">
                        <Lock className="h-9 w-9 text-gold/50 mx-auto" strokeWidth={1.25} />
                        <h2 className="mt-6 font-serif text-2xl font-light text-brand">Administrators only</h2>
                        <p className="mt-4 text-sm text-mist">Sign in with an administrator account to manage orders.</p>
                    </div>
                </section>
            </div>
        );
    }

    const setField = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.put("/admin/settings", {
                shipping_flat_cents: parseInt(settings.shipping_flat_cents, 10) || 0,
                free_shipping_threshold_cents: parseInt(settings.free_shipping_threshold_cents, 10) || 0,
                tax_percent: parseFloat(settings.tax_percent || 0),
                payment_link_fee_percent: parseFloat(settings.payment_link_fee_percent || 0),
                discount_codes: settings.discount_codes || [],
                payment_methods: settings.payment_methods || [],
            });
            toast.success("Checkout settings saved");
            load();
        } catch (err) {
            toast.error("Could not save settings", { description: err.response?.data?.detail || "Please try again." });
        } finally {
            setSaving(false);
        }
    };

    const updateMethod = (i, patch) =>
        setSettings((s) => ({
            ...s,
            payment_methods: s.payment_methods.map((m, j) => (j === i ? { ...m, ...patch } : m)),
        }));

    const updateCode = (i, patch) =>
        setSettings((s) => ({
            ...s,
            discount_codes: s.discount_codes.map((c, j) => (j === i ? { ...c, ...patch } : c)),
        }));

    // shipping and free-shipping threshold are edited in dollars, stored in cents
    const shippingDollars = settings ? (settings.shipping_flat_cents / 100).toFixed(2) : "0.00";
    const freeShipDollars = settings ? ((settings.free_shipping_threshold_cents || 0) / 100).toFixed(2) : "0.00";

    return (
        <div data-testid="admin-page">
            <PageHeader
                eyebrow="Administration"
                title="Orders & Checkout Settings"
                description="Review orders, send payment links, confirm payments, and configure checkout. Payment confirmation is always a separate manual action."
            />
            <section className="pb-24 sm:pb-32 px-6 lg:px-8">
                <div className="max-w-6xl mx-auto space-y-14">
                    {/* Products */}
                    <Reveal>
                        <div>
                            <p className="eyebrow">Products</p>
                            <div className="mt-5">
                                <ProductCreator onSaved={load} />
                            </div>
                            <div className="mt-6 space-y-4" data-testid="admin-products-list">
                                {products === null ? (
                                    <p className="text-sm text-mist">Loading…</p>
                                ) : (
                                    products.map((p) => <ProductEditor key={p.id} product={p} onSaved={load} />)
                                )}
                            </div>
                        </div>
                    </Reveal>

                    {/* Orders */}
                    <Reveal>
                        <div>
                            <p className="eyebrow">Orders</p>
                            <div className="mt-6 space-y-5" data-testid="admin-orders-list">
                                {orders === null ? (
                                    <p className="text-sm text-mist">Loading…</p>
                                ) : orders.length === 0 ? (
                                    <p className="text-sm text-mist border border-dashed border-stone-200 bg-white p-10 text-center">
                                        No orders yet.
                                    </p>
                                ) : (
                                    orders.map((o) => <OrderCard key={o.order_id} order={o} onAction={load} />)
                                )}
                            </div>
                        </div>
                    </Reveal>

                    {/* AI Assistant */}
                    <Reveal delay={0.05}>
                        <AiAssistant products={products} />
                    </Reveal>

                    {/* COA Library */}
                    <Reveal delay={0.05}>
                        <CoaLibrary products={products || []} documents={documents || []} onChanged={load} />
                    </Reveal>

                    {/* Settings */}
                    {settings && (
                        <Reveal delay={0.05}>
                            <div className="bg-white border border-stone-200 p-7 sm:p-10" data-testid="admin-settings">
                                <p className="eyebrow">Checkout Settings</p>

                                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                    <label className="block">
                                        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Flat shipping ($)</span>
                                        <input
                                            type="number" min="0" step="0.01"
                                            value={shippingDollars}
                                            onChange={(e) => setField("shipping_flat_cents", Math.round(parseFloat(e.target.value || 0) * 100))}
                                            data-testid="admin-shipping-input"
                                            className="mt-2 w-full border border-stone-200 px-4 py-3 text-sm focus:border-wine/50 focus:outline-none"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Free shipping over ($)</span>
                                        <input
                                            type="number" min="0" step="1"
                                            value={freeShipDollars}
                                            onChange={(e) => setField("free_shipping_threshold_cents", Math.round(parseFloat(e.target.value || 0) * 100))}
                                            data-testid="admin-free-shipping-input"
                                            className="mt-2 w-full border border-stone-200 px-4 py-3 text-sm focus:border-wine/50 focus:outline-none"
                                        />
                                        <span className="mt-1.5 block text-[0.6rem] text-slate-400">Set to 0 to turn free shipping off.</span>
                                    </label>
                                    <label className="block">
                                        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Tax rate (%)</span>
                                        <input
                                            type="number" min="0" max="30" step="0.1"
                                            value={settings.tax_percent}
                                            onChange={(e) => setField("tax_percent", parseFloat(e.target.value || 0))}
                                            data-testid="admin-tax-input"
                                            className="mt-2 w-full border border-stone-200 px-4 py-3 text-sm focus:border-wine/50 focus:outline-none"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Payment-link fee (%)</span>
                                        <input
                                            type="number" min="0" max="25" step="0.5"
                                            value={settings.payment_link_fee_percent}
                                            onChange={(e) => setField("payment_link_fee_percent", parseFloat(e.target.value || 0))}
                                            data-testid="admin-fee-input"
                                            className="mt-2 w-full border border-stone-200 px-4 py-3 text-sm focus:border-wine/50 focus:outline-none"
                                        />
                                    </label>
                                </div>

                                <p className="eyebrow mt-10">Discount Codes</p>
                                <div className="mt-5 space-y-3" data-testid="admin-discount-codes">
                                    {(settings.discount_codes || []).map((c, i) => (
                                        <div key={i} className="flex gap-3 items-center">
                                            <input
                                                value={c.code}
                                                onChange={(e) => updateCode(i, { code: e.target.value.toUpperCase() })}
                                                placeholder="CODE"
                                                data-testid={`admin-code-${i}`}
                                                className="w-40 border border-stone-200 px-4 py-2.5 text-sm uppercase focus:border-wine/50 focus:outline-none"
                                            />
                                            <input
                                                type="number" min="1" max="90" step="1"
                                                value={c.percent}
                                                onChange={(e) => updateCode(i, { percent: parseFloat(e.target.value || 0) })}
                                                data-testid={`admin-code-percent-${i}`}
                                                className="w-24 border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                                            />
                                            <span className="text-xs text-slate-400">% off subtotal</span>
                                            <button
                                                type="button"
                                                onClick={() => setSettings((s) => ({ ...s, discount_codes: s.discount_codes.filter((_, j) => j !== i) }))}
                                                data-testid={`admin-code-remove-${i}`}
                                                className="text-slate-400 hover:text-red-600 transition-colors"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setSettings((s) => ({ ...s, discount_codes: [...(s.discount_codes || []), { code: "", percent: 10 }] }))}
                                        data-testid="admin-code-add"
                                        className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine hover:text-wine-deep transition-colors"
                                    >
                                        <Plus className="h-3.5 w-3.5" /> Add code
                                    </button>
                                </div>

                                <p className="eyebrow mt-10">Payment Methods</p>
                                <div className="mt-5 space-y-6" data-testid="admin-payment-methods">
                                    {(settings.payment_methods || []).map((m, i) => (
                                        <div key={i} className="border border-stone-200 p-5 space-y-4" data-testid={`admin-method-${m.key}`}>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                <label className="block">
                                                    <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Key (lowercase, no spaces)</span>
                                                    <input
                                                        value={m.key}
                                                        onChange={(e) => updateMethod(i, { key: e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                                                        placeholder="stripe"
                                                        data-testid={`admin-method-key-${i}`}
                                                        className="mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Label</span>
                                                    <input
                                                        value={m.label}
                                                        onChange={(e) => updateMethod(i, { label: e.target.value })}
                                                        data-testid={`admin-method-label-${i}`}
                                                        className="mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">External link (optional, https://)</span>
                                                    <input
                                                        value={m.link_url || ""}
                                                        onChange={(e) => updateMethod(i, { link_url: e.target.value })}
                                                        placeholder="https://…"
                                                        data-testid={`admin-method-link-${i}`}
                                                        className="mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                                                    />
                                                </label>
                                            </div>
                                            <label className="block">
                                                <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Customer instructions</span>
                                                <textarea
                                                    value={m.instructions}
                                                    onChange={(e) => updateMethod(i, { instructions: e.target.value })}
                                                    rows={2}
                                                    data-testid={`admin-method-instructions-${i}`}
                                                    className="mt-2 w-full border border-stone-200 px-4 py-2.5 text-sm focus:border-wine/50 focus:outline-none"
                                                />
                                            </label>
                                            <div className="flex flex-wrap items-center gap-6">
                                                <label className="flex items-center gap-2 text-xs text-slate-600">
                                                    <input type="checkbox" checked={!!m.is_payment_link} onChange={(e) => updateMethod(i, { is_payment_link: e.target.checked })}
                                                        data-testid={`admin-method-islink-${i}`} className="h-4 w-4 accent-[#7A2230]" />
                                                    Payment-link method (fee applies, link sent after review)
                                                </label>
                                                <label className="flex items-center gap-2 text-xs text-slate-600">
                                                    <input type="checkbox" checked={!!m.enabled} onChange={(e) => updateMethod(i, { enabled: e.target.checked })}
                                                        data-testid={`admin-method-enabled-${i}`} className="h-4 w-4 accent-[#7A2230]" />
                                                    Enabled
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setSettings((s) => ({ ...s, payment_methods: s.payment_methods.filter((_, j) => j !== i) }))}
                                                    data-testid={`admin-method-remove-${i}`}
                                                    className="ml-auto text-slate-400 hover:text-red-600 transition-colors"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setSettings((s) => ({
                                            ...s,
                                            payment_methods: [...(s.payment_methods || []), { key: "", label: "", instructions: "", link_url: "", is_payment_link: false, enabled: true }],
                                        }))}
                                        data-testid="admin-method-add"
                                        className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine hover:text-wine-deep transition-colors"
                                    >
                                        <Plus className="h-3.5 w-3.5" /> Add payment method
                                    </button>
                                </div>

                                <div className="mt-10">
                                    <ButtonPrimary onClick={saveSettings} disabled={saving} data-testid="admin-settings-save">
                                        {saving ? "Saving…" : "Save Checkout Settings"}
                                    </ButtonPrimary>
                                </div>
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>
        </div>
    );
}
